import { useEffect } from 'react';

/**
 * Registers a `beforeunload` handler while `isDirty` is true so the browser
 * prompts before a refresh / tab-close / external navigation discards unsaved
 * form state. Removes the handler when `isDirty` goes false or the component
 * unmounts.
 *
 * NOTE: `beforeunload` only covers full-page unloads. It does NOT intercept
 * in-app react-router navigation — blocking that requires a router blocker and
 * is intentionally out of scope. Pair this with `useFormDirty` for the dirty
 * signal.
 */
export function useBeforeUnloadWarning(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers require returnValue to be set for the prompt to show.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);
}
